// Copied from @edulead/utils/src/utils/TitleConfig.ts
// Keep in sync with backend enums

export const DECISION_LEVELS = [
  { value: 'executive', label: 'Executive' },
  { value: 'functional leader', label: 'Functional Leader' },
  { value: 'school leadership', label: 'School Leadership' },
  { value: 'operational support', label: 'Operational Support' },
  { value: 'practitioner', label: 'Practitioner' },
] as const;

export const FUNCTIONAL_GROUPS = [
  { value: 'district administration', label: 'District Administration' },
  { value: 'finance and procurement', label: 'Finance & Procurement' },
  { value: 'human resources', label: 'Human Resources' },
  { value: 'technology', label: 'Technology' },
  { value: 'operations', label: 'Operations' },
  { value: 'curriculum and instruction', label: 'Curriculum & Instruction' },
  { value: 'special education', label: 'Special Education' },
  { value: 'student services', label: 'Student Services' },
] as const;

// From @edulead/utils/src/utils/TitleConfig.ts - MarketTheme enum
export const MARKET_THEMES = [
  { value: 'athletics', label: 'Athletics' },
  { value: 'accountability', label: 'Accountability' },
  { value: 'career readiness', label: 'Career Readiness' },
  { value: 'community and family engagement', label: 'Community & Family Engagement' },
  { value: 'diversity equity and inclusion', label: 'DEI' },
  { value: 'early learning', label: 'Early Learning' },
  { value: 'enrollment', label: 'Enrollment' },
  { value: 'gifted education', label: 'Gifted Education' },
  { value: 'governance', label: 'Governance' },
  { value: 'facilities', label: 'Facilities' },
  { value: 'finance', label: 'Finance' },
  { value: 'fine arts', label: 'Fine Arts' },
  { value: 'human resources', label: 'Human Resources' },
  { value: 'literacy', label: 'Literacy' },
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'multilingual learner', label: 'Multilingual Learner' },
  { value: 'professional development', label: 'Professional Development' },
  { value: 'public relations', label: 'Public Relations' },
  { value: 'school operations', label: 'School Operations' },
  { value: 'science', label: 'Science' },
  { value: 'social emotional learning', label: 'SEL' },
  { value: 'special education', label: 'Special Education' },
  { value: 'stem', label: 'STEM' },
  { value: 'student health services', label: 'Student Health Services' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'world languages', label: 'World Languages' },
  { value: 'adult education', label: 'Adult Education' },
  { value: 'advancement', label: 'Advancement' },
  { value: 'digital media', label: 'Digital Media' },
  { value: 'faith formation', label: 'Faith Formation' },
  { value: 'instructional technology', label: 'Instructional Technology' },
  { value: 'international programs', label: 'International Programs' },
  { value: 'intervention', label: 'Intervention' },
  { value: 'library', label: 'Library' },
  { value: 'physical education', label: 'Physical Education' },
  { value: 'social studies', label: 'Social Studies' },
  { value: 'strategic planning', label: 'Strategic Planning' },
  { value: 'student life', label: 'Student Life' },
  { value: 'technology', label: 'Technology' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'business marketing and finance', label: 'Business, Marketing & Finance' },
  { value: 'hospitality tourism and management', label: 'Hospitality, Tourism & Management' },
] as const;

export const ACCESS_TYPES = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'PRIVATE', label: 'Private' },
] as const;

// Email verification statuses considered "bad" (non-deliverable) - always excluded from buying groups
const BAD_EMAIL_STATUSES = ['INVALID', 'ABUSE', 'DO_NOT_MAIL', 'SPAMTRAP', 'UNKNOWN', 'DISPOSABLE'];

// Employment statuses considered "past" - excluded from buying groups (interim is fine)
// Matches PastStatuses from @edulead/utils/src/utils/TitleConfig.ts
const PAST_STATUSES = ['former', 'past', 'retired', 'departed', 'resigned', 'emeritus'];

export interface BuyingGroupQuery {
  marketThemes?: string[];
  states?: string[];
  functionalGroups?: string[];
  decisionLevels?: string[];
  accessTypes?: string[];
  nrStudentsMin?: number;
  nrStudentsMax?: number | null;
}

// Maps array-based BuyingGroupQuery fields to API field names
type ArrayFilterKey = 'marketThemes' | 'states' | 'functionalGroups' | 'decisionLevels' | 'accessTypes';
const FIELD_MAP: Record<ArrayFilterKey, string> = {
  marketThemes: 'role.marketThemes',
  states: 'institution.state',
  functionalGroups: 'role.functionalGroup',
  decisionLevels: 'role.decisionLevel',
  accessTypes: 'institution.access',
};

// Operator to use for each array field
const OPERATOR_MAP: Record<ArrayFilterKey, string> = {
  marketThemes: 'in',
  states: 'equals',  // institution.state only supports equals/not_equals
  functionalGroups: 'in',
  decisionLevels: 'in',
  accessTypes: 'equals',  // institution.access only supports equals/not_equals
};

/**
 * Converts BuyingGroupQuery to q string (base64 JSON with { rules: [...] })
 * Same format as People Directory
 */
export function serializeBuyingGroupQuery(query: BuyingGroupQuery): string {
  const rules = [];

  // Handle array-based filters
  for (const key of Object.keys(FIELD_MAP) as ArrayFilterKey[]) {
    const values = query[key];
    if (values && values.length > 0) {
      const operator = OPERATOR_MAP[key];
      // For 'equals' operator, use comma-separated string; for 'in', use array
      const value = operator === 'equals' ? values.join(',') : values;
      rules.push({
        field: FIELD_MAP[key],
        operator,
        value,
      });
    }
  }

  // Handle nr_students range filter (from histogram)
  // Uses accountNrStudents (account-level field) to filter by district size, not individual school size
  if (query.nrStudentsMin !== undefined || query.nrStudentsMax !== undefined) {
    const min = query.nrStudentsMin ?? 0;
    const max = query.nrStudentsMax;
    if (max !== null && max !== undefined) {
      // Bounded range: use between
      rules.push({
        field: 'accountNrStudents',
        operator: 'between',
        value: [min, max],
      });
    } else if (min > 0) {
      // Unbounded upper: use gte
      rules.push({
        field: 'accountNrStudents',
        operator: 'gte',
        value: min,
      });
    }
  }

  // Always exclude bad emails from buying groups
  rules.push({
    field: 'emailVerificationStatus',
    operator: 'not_in',
    value: BAD_EMAIL_STATUSES,
  });

  // Always exclude stale contacts (orange dot = older than 12 months)
  rules.push({
    field: 'lastSeen',
    operator: 'equals',
    value: 'LAST_SEEN_0_12',
  });

  // Always exclude past employment statuses from buying groups
  // This is in the same nested group as other role filters (marketThemes, decisionLevel, etc.)
  rules.push({
    field: 'role.status',
    operator: 'not_in',
    value: PAST_STATUSES,
  });

  if (rules.length === 0) return '';
  return btoa(JSON.stringify({ rules }));
}

/**
 * Parses q string back to BuyingGroupQuery for UI state
 */
export function parseBuyingGroupQuery(q: string | null): BuyingGroupQuery {
  if (!q) return {};
  try {
    const parsed = JSON.parse(atob(q));
    const result: BuyingGroupQuery = {};

    for (const rule of parsed.rules || []) {
      if (rule.field === 'role.marketThemes') {
        result.marketThemes = Array.isArray(rule.value) ? rule.value : [rule.value];
      } else if (rule.field === 'institution.state') {
        // States use comma-separated string with 'equals' operator
        const val = rule.value;
        result.states = typeof val === 'string' ? val.split(',') : (Array.isArray(val) ? val : [val]);
      } else if (rule.field === 'role.functionalGroup') {
        result.functionalGroups = Array.isArray(rule.value) ? rule.value : [rule.value];
      } else if (rule.field === 'role.decisionLevel') {
        result.decisionLevels = Array.isArray(rule.value) ? rule.value : [rule.value];
      } else if (rule.field === 'institution.access') {
        const val = rule.value;
        result.accessTypes = typeof val === 'string' ? val.split(',') : (Array.isArray(val) ? val : [val]);
      } else if (rule.field === 'accountNrStudents') {
        if (rule.operator === 'between' && Array.isArray(rule.value)) {
          result.nrStudentsMin = rule.value[0];
          result.nrStudentsMax = rule.value[1];
        } else if (rule.operator === 'gte') {
          result.nrStudentsMin = rule.value;
          result.nrStudentsMax = null;
        }
      }
      // emailVerificationStatus and lastSeen filters are always added automatically, no need to parse
    }

    return result;
  } catch {
    return {};
  }
}
